import React, { useEffect, useId, useState } from 'react';
import { Popover } from 'antd';
import { CloseOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import './AutomatedResultsNotice.css';

const AutomatedResultsNotice = ({ variant = 'banner' }) => {
    const { t } = useTranslation('review');
    const [open, setOpen] = useState(false);
    const popoverId = useId();
    const isHeader = variant === 'header';

    useEffect(() => {
        if (!open) return undefined;

        const handleEscape = (event) => {
            if (event.key === 'Escape') setOpen(false);
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [open]);

    const triggerProps = {
        'aria-controls': open ? popoverId : undefined,
        'aria-expanded': open,
        'aria-haspopup': 'dialog',
        onKeyDown: (event) => {
            if (event.key === 'Escape') setOpen(false);
        },
    };

    const content = (
        <div
            id={popoverId}
            className="automated-results-popover"
            role="dialog"
            aria-label={t('sidebar.automatedResults.detailsTitle')}
        >
            <div className="automated-results-popover__header">
                <strong>{t('sidebar.automatedResults.detailsTitle')}</strong>
                <button
                    type="button"
                    className="automated-results-popover__close"
                    onClick={() => setOpen(false)}
                    aria-label={t('sidebar.automatedResults.close')}
                >
                    <CloseOutlined aria-hidden="true" />
                </button>
            </div>
            <p>{t('sidebar.automatedResults.details')}</p>
        </div>
    );

    const popover = (trigger) => (
        <Popover
            content={content}
            trigger="click"
            placement={isHeader ? 'bottomLeft' : 'topLeft'}
            open={open}
            onOpenChange={setOpen}
            classNames={{ root: 'automated-results-popover-overlay' }}
            destroyOnHidden
        >
            {trigger}
        </Popover>
    );

    if (isHeader) {
        return popover(
            <button
                type="button"
                className="automated-results-indicator"
                aria-label={t('sidebar.automatedResults.headerAriaLabel')}
                {...triggerProps}
            >
                <span className="automated-results-indicator__label">
                    {t('sidebar.automatedResults.headerLabel')}
                </span>
                <InfoCircleOutlined aria-hidden="true" />
            </button>
        );
    }

    return (
        <aside className="automated-results-banner" role="note">
            <InfoCircleOutlined
                className="automated-results-banner__icon"
                role="img"
                aria-label={t('sidebar.automatedResults.iconLabel')}
            />
            <div className="automated-results-banner__copy">
                <span>{t('sidebar.automatedResults.short')}</span>{' '}
                {popover(
                    <button
                        type="button"
                        className="automated-results-banner__more"
                        {...triggerProps}
                    >
                        {t('sidebar.automatedResults.more')}
                    </button>
                )}
            </div>
        </aside>
    );
};

export default AutomatedResultsNotice;
